import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { AppShell, Button } from '@cloudsam/ui';
import { getServiceUrl } from './sysconfig';
import SamLogo from './components/SamLogo';
import { openHelpAgent, notifyHelpAgent } from './utils/openHelpAgent';
import { HelpCircle } from 'lucide-react';
import {
  addRecipientInCloud,
  getSurveySummaryFromCloud,
  getSurveyVersionFromCloud,
  setOrgContext
} from './utils/cloudApi';
import { fetchResultsJson } from './utils/cloudApi';
import { formatUndeployConfirmation } from './utils/lifecycleUtils';
import { createSurveyInCloud } from './utils/cloudApi';
import { linkUserToDeployment, unlinkUserFromDeployment, updateUser } from './utils/consoleApi';

import SamSpinner from './components/SamSpinner';

import UsersPanel from './components/UsersPanel';
import { validateResultsCsv, downloadTextFile } from './utils/csvUtils';
import { buildResultsCsv } from './utils/resultsCsv';
import { useConnectivity } from './hooks/useConnectivity';
import { useSystemStatus } from './hooks/useSystemStatus';
import { SystemStatusModal } from './components/SystemStatusModal';
import { StatusBanners } from './components/StatusBanners';
import { PromptModal } from './components/PromptModal';
import { NapModal } from './components/NapModal';
import { SendModal } from './components/SendModal';
import DeploymentRecipientsModal from './components/DeploymentRecipientsModal';
import { CommandBar } from './components/CommandBar';
import ConfirmModal from './components/ConfirmModal';
import ArchiveOverwriteDialog from './components/ArchiveOverwriteDialog';
import SendConfirmModal from './components/SendConfirmModal';
import SurveyBrowser from './components/SurveyBrowser';
import StagingArea from './components/StagingArea';
import ExecutedSurveysPanel from './components/ExecutedSurveysPanel';
import ChannelStatusPanel from './components/ChannelStatusPanel';
import { ReadyToDeployPanel, DeployedPanel } from './components/SurveyListPanels';
import { NotificationBanners } from './components/NotificationBanners';
import { useAuth } from './context/AuthContext';
import { AccountPanel } from './components/AccountPanel';
import DashboardPanel from './components/dashboard/DashboardPanel';
import useStagingArea from './hooks/useStagingArea';
import usePresentorWindows from './hooks/usePresentorWindows';
import useSRA from './hooks/useSRA';
import useSurveyData from './hooks/useSurveyData';
import useSurveyLifecycle from './hooks/useSurveyLifecycle';
import useArchive from './hooks/useArchive';
import useBillingGate from './hooks/useBillingGate';
import BillingGateOverlays from './components/BillingGateOverlays';
import VerticalSplitter from './components/VerticalSplitter';
import HorizontalSplitter from './components/HorizontalSplitter';
import Toast from './components/Toast';
import IncomingSharesNotice from './components/IncomingSharesNotice';
import { isBillingAccessActive } from './billing-access';
import { getChannelControlStatus, refreshChannelHealth } from './utils/channelControlApi';


export default function ConsoleApp() {
  // authLoading = true while checkAuth() is resolving; false once the user
  // profile has been fetched. Aliased to avoid collision with local `loading`.
  const { user, logout, loading: authLoading } = useAuth();
  // Bridge refs so the staging hook can call setError / refresh, which are
  // declared later in this component. Assigned in the render body below once
  // those are defined.
  const stagingErrorRef = useRef(null);
  const stagingRefreshRef = useRef(null);
  const [channelControls, setChannelControls] = useState(null);
  const [channelControlError, setChannelControlError] = useState('');
  useEffect(() => {
    if (!user?.org_id) {
      setChannelControls(null);
      setChannelControlError('');
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const rows = await getChannelControlStatus();
        if (!cancelled) {
          setChannelControls(rows);
          setChannelControlError('');
        }
      } catch (err) {
        if (!cancelled) {
          setChannelControls(null);
          setChannelControlError(err.message || 'Channel enablement is unavailable.');
        }
      }
    };
    load();
    const timer = window.setInterval(load, 60000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [user?.org_id]);
  useEffect(() => {
    if (!user?.org_id) return undefined;
    let cancelled = false;
    let timer = null;
    const run = async () => {
      const started = Date.now();
      let duration = 0;
      try {
        const result = await refreshChannelHealth();
        duration = Number(result?.duration_ms) || (Date.now() - started);
        const rows = await getChannelControlStatus();
        if (!cancelled) {
          setChannelControls(rows);
          setChannelControlError('');
        }
      } catch (err) {
        // Keep the last completed readings. The ordinary one-minute status read
        // will surface service-level availability separately.
        console.warn('Channel health refresh failed:', err);
        duration = Date.now() - started;
      }
      if (!cancelled) {
        const delay = duration >= 60000 ? 15 * 60 * 1000 : 10 * 60 * 1000;
        timer = window.setTimeout(run, delay);
      }
    };
    run();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [user?.org_id]);
  // Bridge the sent → re-deploy orchestration into the staging hook, so the
  // hook's linkSurvey 409 fallback (server rejects staging a 'sent' survey) can
  // route into reSendSentSurvey, which is defined later in this component.
  const stagingSentConflictRef = useRef(null);
  const staging = useStagingArea({
    orgId: user?.org_id || '',
    onError: (msg) => stagingErrorRef.current?.(msg),
    onAfterTransition: () => stagingRefreshRef.current?.(),
    onSentConflict: (survey) => stagingSentConflictRef.current?.(survey),
    channelControls,
  });

  // Auto-track: tell sam-dbam we're in Console whenever this tab gets focus
  useEffect(() => {
    const notify = () => notifyHelpAgent('console', user?.user_id);
    window.addEventListener('focus', notify);
    return () => window.removeEventListener('focus', notify);
  }, [user?.user_id]);

  // The recipient-metadata backfill that used to live here is gone. It existed
  // because the staged list came from this browser's localStorage, which held
  // ids with no names attached. sam-dbam now returns the name, email, phone and
  // channel with the list itself, so there is nothing left to fill in.

  // Set org context as soon as the auth check completes and a user is known.
  // Guarded on !authLoading to ensure setOrgContext is only called with a
  // real org_id, never with the initial null user.
  //
  // DELIBERATELY called in the render body, not in an effect (Step 6 review):
  // React runs child effects before parent effects, so child panels (e.g.
  // UsersPanel) fire their data loads before any parent effect could run.
  // Render-body code runs before children render at all, so this is the only
  // placement that guarantees org context is set before every child API call
  // (403 MISSING_ORG_CONTEXT otherwise). The call is idempotent, so re-running
  // each render (and twice under StrictMode) is harmless.
  if (!authLoading && user?.org_id) {
    setOrgContext(user.org_id, user.user_id);
  }
  // Diagnostic: log org-context state on every render so we can trace timing
  // issues in the browser console. Step 6 tidy: now dev-only, matching every
  // other diagnostic in this file.
  if (process.env.NODE_ENV !== 'production') { try { console.log('[ConsoleApp] render', { authLoading, org_id: user?.org_id || null, user_id: user?.user_id || null }); } catch (_) { } }

  const [selectedSurveyId, setSelectedSurveyId] = useState(null);
  const [selectedDeploymentId, setSelectedDeploymentId] = useState(null);
  const [selectedRecipientIds, setSelectedRecipientIds] = useState(new Set());
  const [showSendModal, setShowSendModal] = useState(null);
  const [showDeploymentRecipientsModal, setShowDeploymentRecipientsModal] = useState(false);
  // ConfirmModal state — replaces window.confirm() so the dialog title
  // doesn't show the Cloud Run hostname. Pattern: caller populates this
  // object to open the modal; cleared to null to close. (BUG-113)
  const [confirmModal, setConfirmModal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Export (CSV) error and retry controls
  const [exportError, setExportError] = useState(null); // { message, deploymentId }
  const [centerRatio, setCenterRatio] = useState(0.4);
  const [centerRightRatio, setCenterRightRatio] = useState(0.75);
  const [rightColumnRatio, setRightColumnRatio] = useState(0.5);
  // Users tab state (now managed in UsersPanel component)

  // Removed BroadcastChannel/requestFocus: named windows provide reliable reuse
  // SRA (Session Results Analyzer) state and effects live in hooks/useSRA.js
  // (refactor Step 3) — the hook is instantiated below, after lastSnapshotRef.
  // Dropdown menus for command bar
  const [showUsersMenu, setShowUsersMenu] = useState(false);
  const [showSurveyBrowser, setShowSurveyBrowser] = useState(false);
  const [showAccountPanel, setShowAccountPanel] = useState(false);
  const [accountInitialSection, setAccountInitialSection] = useState('profile');
  // Billing-access gate — state, first blocking check, and 30s silent re-poll
  // live in the hook (refactor Step 1). Same { loading, access, error } shape.
  const { billingGate, refreshBillingGate } = useBillingGate({ authLoading, user });
  const [showDashboard, setShowDashboard] = useState(true); // show on login
  // Ops-facing persistence failure banner
  const [persistenceFailure, setPersistenceFailure] = useState(null); // { sessionId, reason, ts }
  // Phase 19: degraded connectivity. The hook owns isOnline, the online/offline
  // listeners, and the retry queue (replayed on reconnect) — refactor Step 2.
  // Previously this logic was duplicated inline here while the hook sat unused.
  const { isOnline, retryQueueRef } = useConnectivity();
  const usersPanelRef = useRef(null);

  const openAccountPanel = useCallback((section = 'profile') => {
    setAccountInitialSection(section);
    setShowAccountPanel(true);
  }, []);

  // Survey/deployment data layer (refactor Step 5a): surveys, deployments,
  // recipients, offline snapshot, recipients cache, and the WP3 derived
  // lifecycle views. Owns the first fetch (post-auth) and refresh().
  const {
    surveys,
    deployments,
    deploymentCounts, rememberDeploymentCounts,
    recipients,
    recipientsByDeployment,
    search, setSearch,
    corsBanner,
    isOpenDeployment,
    surveyStatusById,
    stagedSurveys,
    stagedSignature,
    readyToDeploy,
    lastSnapshotRef,
    refresh,
    loadRecipients,
  } = useSurveyData({ authLoading, user, isOnline, retryQueueRef, setLoading, onError: setError });

  // Selection + recipient load, composed: panels select a deployment and load
  // its recipients in one call (previous onLoadRecipients behavior).
  const onLoadRecipients = useCallback(async (deploymentId) => {
    setSelectedDeploymentId(deploymentId);
    await loadRecipients(deploymentId);
  }, [loadRecipients]);

  // SRA — Session Results Analyzer (refactor Step 3). A discrete module:
  // sessions, exchange timelines, prompts/NAP, results view, email lookup.
  const {
    sessions,
    selectedSessionIds, setSelectedSessionIds,
    exchanges,
    promptModal, setPromptModal,
    napModal, setNapModal,
    variableByQuestionId,
    participantEmailMap,
    dataFreshness,
    csvFormat,
    deleteSessions,
  } = useSRA({
    selectedDeploymentId,
    selectedSurveyId,
    deployments,
    lastSnapshotRef,
    onError: (msg) => setError(msg),
  });

  // Archive support (refactor Step 4): open archive file, write-back channel,
  // archive actions, and the overwrite-consent dialog state.
  const {
    archiveFileOpen, setArchiveFileOpen,
    archiveHasContent, setArchiveHasContent,
    archiveProgress,
    overwriteModal, setOverwriteModal,
    onArchiveSurvey,
    onBulkArchiveSurveys,
  } = useArchive({
    surveys,
    surveyStatusById,
    refresh,
    setLoading,
    onError: setError,
    setConfirmModal,
    openAccountPanel,
  });

  // Survey lifecycle actions (refactor Step 5b): deploy, undeploy, re-deploy,
  // re-send, share, copy, delete, custom slug — plus the WP3 undeploy
  // confirmation and the re-deploy modal state.
  const {
    undeployPrompt,
    resolveUndeploy,
    redeployModal, setRedeployModal,
    deleteProgress,
    onDeploy,
    onShareSurvey,
    onSetCustomSlug,
    onUndeploy,
    reSendSentSurvey,
    onRedeploy,
    onConfirmRedeploy,
    onDeleteSurvey,
    onBulkDeleteSurveys,
    onCopy,
  } = useSurveyLifecycle({
    user,
    surveys,
    staging,
    refresh,
    rememberDeploymentCounts,
    selectedSurveyId,
    onSelectSurvey: setSelectedSurveyId,
    onSelectDeployment: setSelectedDeploymentId,
    setLoading,
    onError: setError,
    setConfirmModal,
    refreshDeploymentUsers: async () => {
      if (usersPanelRef.current?.loadDeploymentUsers) {
        await usersPanelRef.current.loadDeploymentUsers();
      }
    },
  });
  // Keep the staging hook's sent-conflict bridge pointed at the current handler.
  stagingSentConflictRef.current = reSendSentSurvey;

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showUsersMenu) {
        const target = e.target;
        const isInsideDropdown = target.closest('[data-dropdown]');
        if (!isInsideDropdown) {
          setShowUsersMenu(false);
        }
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showUsersMenu]);

  // (Connectivity listeners and retry replay live in useConnectivity — Step 2.)

  // System Status — hook handles all 8-service polling
  const { services: statusServices, aggregateStatus, lastCheckedAt, manualRefresh } = useSystemStatus({ corsBanner });
  const [showStatusModal, setShowStatusModal] = useState(false);

  // Presentor popup management (refactor Step 6): named screen-aware popup,
  // window tracking by recipient, and the presentor:completed listener.
  const { openPresentor } = usePresentorWindows({
    selectedDeploymentId,
    refreshDeploymentUsers: async () => {
      if (usersPanelRef.current?.loadDeploymentUsers) {
        await usersPanelRef.current.loadDeploymentUsers();
      }
    },
    onPersistenceFailure: setPersistenceFailure,
  });


  // Keep the staging hook's bridges pointed at the current setError / refresh.
  // Refs, so re-assigning every render is cheap and always current.
  stagingErrorRef.current = setError;
  stagingRefreshRef.current = refresh;



  // Reconcile the Staging Area's linked-survey list against the server on every
  // load/refresh: derive it from surveys.status — add server-staged surveys,
  // drop entries the server no longer considers staged (with a visible notice).
  useEffect(() => {
    staging.reconcileLinked(stagedSurveys);
    // reconcileLinked is stable (useCallback []); re-run only when the staged
    // set changes (keyed by stagedSignature).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stagedSignature]);



  // (SystemStatus polling is now handled by useSystemStatus hook above)


  // Selection change logging (dev-only)
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') { try { console.info('[Selection]', 'survey selected', { surveyId: selectedSurveyId }); } catch (_) { } }
  }, [selectedSurveyId]);
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') { try { console.info('[Selection]', 'deployment selected', { deploymentId: selectedDeploymentId }); } catch (_) { } }
  }, [selectedDeploymentId]);
  // (Reverted) Presentor window management – keep default behavior
  // Users tab state management moved to UsersPanel component

  const executedSurveys = useMemo(() => {
    // Show all surveys that have an open deployment
    const openDeps = (deployments || []).filter(isOpenDeployment);
    if (openDeps.length === 0) return [];
    const byId = Object.fromEntries((surveys || []).map(s => [s.survey_id, s]));
    // Count recipients per survey for display
    const takersBySurvey = {};
    openDeps.forEach(dep => {
      const recs = recipientsByDeployment.get(dep.deployment_id) || [];
      const takers = recs.filter(r => r.status === 'started' || r.status === 'completed').length;
      takersBySurvey[dep.survey_id] = (takersBySurvey[dep.survey_id] || 0) + takers;
    });
    // Count sessions for the currently selected survey
    const sessionCount = sessions.length;
    // Bug 8 fix: use cached session counts for unselected surveys
    const cachedSessions = (lastSnapshotRef.current && lastSnapshotRef.current.sessionsBySurveyId) || {};
    const list = Array.from(new Set(openDeps.map(d => d.survey_id))).map(id => {
      const s = byId[id] || { survey_id: id, name: id };
      const takers = takersBySurvey[id] || 0;
      // If this is the selected survey, use live session count
      // Otherwise, try cached session count, then fall back to takers
      let count;
      if (selectedSurveyId === id && sessionCount > 0) {
        count = sessionCount;
      } else {
        const cached = cachedSessions[String(id)];
        count = (Array.isArray(cached) && cached.length > 0) ? cached.length : takers;
      }
      return { ...s, takers: count };
    }).filter(s => s.takers > 0); // Only show surveys where at least one recipient has started
    return list;
  }, [surveys, deployments, recipientsByDeployment, isOpenDeployment, sessions, selectedSurveyId, dataFreshness]);


  const onDeleteSessions = async () => {
    if (!selectedSessionIds || selectedSessionIds.size === 0) return;
    setConfirmModal({
      title: 'Delete sessions?',
      message: `Permanently delete ${selectedSessionIds.size} session(s)?`,
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        setConfirmModal(null);
        setLoading(true); setError('');
        try {
          // Server deletion + local state pruning live in the SRA module.
          await deleteSessions(selectedSessionIds);
        } catch (e) {
          setError(e.message);
        } finally { setLoading(false); }
      }
    });
  };


  // Export the results CSV. CSV construction is the pure buildResultsCsv
  // utility (utils/resultsCsv.js, unit-tested). Behavior fix in refactor
  // Step 3: the pre-refactor code contained this whole routine twice — once
  // behind the confirmation modal and once unconditionally after it, so an
  // export could run twice (or skip confirmation). The contract is now:
  // sessions selected → export immediately; none selected → confirm first;
  // either way the export runs exactly once.
  const onExportResultsCsv = async ({ extended = false, format = 'long' } = {}) => {
    if (!selectedDeploymentId) return;
    setExportError(null);

    // Derive recipient IDs from selected sessions
    let recipientIds = null;
    if (selectedSessionIds && selectedSessionIds.size > 0) {
      recipientIds = Array.from(new Set(
        Array.from(selectedSessionIds)
          .map(sessionId => {
            const session = sessions.find(s => s.session_id === sessionId);
            return session?.participant_id;
          })
          .filter(Boolean)
      ));
    }

    const runExport = async () => {
      try {
        setLoading(true);

        // Fetch results from the existing JSON endpoint (which works)
        const allRows = await fetchResultsJson(selectedDeploymentId, { timeoutMs: extended ? 20000 : 8000 });
        let rows = Array.isArray(allRows) ? allRows : [];

        // Filter by selected recipient IDs if applicable
        if (recipientIds && recipientIds.length > 0) {
          const idSet = new Set(recipientIds);
          rows = rows.filter(r => idSet.has(r.recipient_id));
        }

        if (rows.length === 0) {
          setExportError({ message: 'No results to export', deploymentId: selectedDeploymentId });
          return;
        }

        const csv = buildResultsCsv(rows, { format, emailByRecipient: participantEmailMap });

        const validation = validateResultsCsv(csv);
        if (!validation.ok) {
          setExportError({ message: `CSV validation failed: ${validation.reason}. You can download the raw file.`, deploymentId: selectedDeploymentId, rawText: csv });
          return;
        }
        const baseName = (surveyName || 'results').replace(/[^A-Za-z0-9_-]+/g, '_');
        const sessionCount = recipientIds ? recipientIds.length : 'all';
        downloadTextFile(csv, `${baseName}_${sessionCount}_${format}.csv`);
      } catch (e) {
        const msg = e && e.message ? e.message : 'Export failed';
        setExportError({ message: msg, deploymentId: selectedDeploymentId });
      } finally {
        setLoading(false);
      }
    };

    if (recipientIds && recipientIds.length > 0) {
      await runExport();
    } else {
      setConfirmModal({
        title: 'Export sessions?',
        message: 'Export all listed sessions?',
        confirmLabel: 'Export',
        onConfirm: async () => {
          setConfirmModal(null);
          await runExport();
        }
      });
    }
  };



  // User selection state (managed by UsersPanel, exposed via callback)
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [selectedUsersData, setSelectedUsersData] = useState([]);

  // Callback from UsersPanel when selection changes
  const handleUserSelectionChange = useCallback((userIds, usersData) => {
    setSelectedUserIds(userIds);
    setSelectedUsersData(usersData);
  }, []);

  const stagingRecipientDisplayNames = useMemo(() => {
    // Primary source: the staging hook's recipientMeta sidecar, populated when
    // the Recipients overlay commits a selection. This survives overlay-close
    // and page reloads. Falls back to deployment-linked `recipients` (for legacy
    // entries) and to the live overlay selection (pre-commit edits).
    const map = { ...(staging?.recipientMeta || {}) };
    for (const r of (recipients || [])) {
      const id = r.global_recipient_id || r.user_id || r.id;
      if (!id || map[id]) continue;
      map[id] = {
        displayName: r.name || r.email || id,
        email: r.email || '',
        channel: r.preferred_channel || 'email',
      };
    }
    for (const userData of (selectedUsersData || [])) {
      const id = userData?.user_id;
      if (!id) continue;
      map[id] = {
        displayName: userData.name || userData.email || id,
        email: userData.email || '',
        channel: userData.preferred_channel || map[id]?.channel || 'email',
      };
    }
    return map;
  }, [staging?.recipientMeta, recipients, selectedUsersData]);

  // Toolbar operations using selection data from UsersPanel
  const onLinkSelectedUsers = async () => {
    if (!selectedDeploymentId || selectedUserIds.size === 0) return;
    setLoading(true); setError('');
    try {
      for (const uid of Array.from(selectedUserIds)) {
        const userData = selectedUsersData.find(u => u.user_id === uid) || {};
        await linkUserToDeployment(selectedDeploymentId, uid, userData.email || '', userData.name || '');
      }
      // Refresh UsersPanel's deployment users to show new link URLs
      if (usersPanelRef.current?.loadDeploymentUsers) {
        await usersPanelRef.current.loadDeploymentUsers();
      }
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const onRevokeSelectedUsers = async () => {
    if (!selectedDeploymentId || selectedUserIds.size === 0) return;
    setLoading(true); setError('');
    try {
      // Unstage ALL selected users from the deployment (not just linked ones)
      for (const uid of selectedUserIds) {
        try { await unlinkUserFromDeployment(selectedDeploymentId, uid); }
        catch (e) { /* ignore 404 for already-unlinked */ }
      }
      // Refresh UsersPanel's deployment users to reflect removal
      if (usersPanelRef.current?.loadDeploymentUsers) {
        await usersPanelRef.current.loadDeploymentUsers();
      }
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  // Unstage: set staged=false and revoke deployment links
  const onUnstageSelectedUsers = async () => {
    if (selectedUserIds.size === 0) return;
    setConfirmModal({
      title: 'Unstage recipients?',
      message: `Unstage ${selectedUserIds.size} recipient(s)?`,
      confirmLabel: 'Unstage',
      danger: true,
      onConfirm: async () => {
        setConfirmModal(null);
        setLoading(true); setError('');
        try {
          for (const uid of Array.from(selectedUserIds)) {
            try {
              // 1. Clear global staged flag
              await updateUser(uid, { staged: false });
              // 2. Revoke deployment link (if any)
              if (selectedDeploymentId) {
                await unlinkUserFromDeployment(selectedDeploymentId, uid);
              }
            } catch (e) { /* ignore 404 for already-removed */ }
          }
          // Refresh staged recipients (panel data source)
          if (usersPanelRef.current?.loadUsers) {
            await usersPanelRef.current.loadUsers();
          }
          // Also refresh deployment users for link column
          if (usersPanelRef.current?.loadDeploymentUsers) {
            await usersPanelRef.current.loadDeploymentUsers();
          }
          // Clear selection in both App and UsersPanel
          setSelectedUserIds(new Set());
          setSelectedUsersData([]);
          if (usersPanelRef.current?.clearSelection) {
            usersPanelRef.current.clearSelection();
          }
        } catch (e) { setError(e.message); }
        finally { setLoading(false); }
      }
    });
  };

  // eslint-disable-next-line no-unused-vars
  const onAddRecipient = async (deploymentId, email, name) => {
    setLoading(true); setError('');
    try {
      await addRecipientInCloud({ deploymentId, email, name });
      // Persist last used recipients in session for convenience
      try {
        const key = 'console_recent_recipients';
        const prev = JSON.parse(sessionStorage.getItem(key) || '[]');
        const next = Array.from(new Set([`${email}|${name || ''}`, ...prev])).slice(0, 10);
        sessionStorage.setItem(key, JSON.stringify(next));
      } catch (_) { }
      // Refresh UsersPanel's deployment users to show the new recipient
      if (usersPanelRef.current?.loadDeploymentUsers) {
        await usersPanelRef.current.loadDeploymentUsers();
      }
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line no-unused-vars
  const onExportCloudsam = async (surveyId) => {
    try {
      const summary = await getSurveySummaryFromCloud(surveyId);
      const ver = summary?.latestVersion?.version_number || 1;
      const { data } = await getSurveyVersionFromCloud(surveyId, ver);
      const payload = data && data.designer_payload_json;
      if (!payload) throw new Error('designer_payload_json missing; re-save in Designer, then retry');
      const name = summary?.survey?.name || surveyId;
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${name}_v${ver}.cloudsam`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch (e) { setError(e.message); }
  };

  // Designer URL: dynamically resolved from sysconfig
  const openDesigner = async (surveyId) => {
    const designerUrl = await getServiceUrl('designer');
    const url = `${designerUrl}/?surveyId=${encodeURIComponent(surveyId)}&ver=latest`;
    window.open(url, 'CloudSamDesigner');
  };

  // Opens the Designer with no surveyId so it starts on a blank "New Survey"
  // canvas. Used by ReadyToDeployPanel's "+ New" action (BUG-104).
  const onNewSurvey = async () => {
    const designerUrl = await getServiceUrl('designer');
    window.open(designerUrl, 'CloudSamDesigner');
  };

  // Explorer URL: dynamically resolved from sysconfig
  const openExplorer = async ({ blank = false } = {}) => {
    const explorerUrl = await getServiceUrl('explorer');
    if (!explorerUrl) {
      setError('Survey Explorer URL not found in sysconfig');
      return;
    }
    const params = new URLSearchParams();
    if (!blank) {
      if (selectedDeploymentId) params.set('deploymentId', selectedDeploymentId);
      if (selectedSurveyId) params.set('surveyId', selectedSurveyId);
    }
    const url = `${explorerUrl}${params.toString() ? '?' + params.toString() : ''}`;
    window.open(url, 'CloudSamExplorer');
  };

  // Selection-driven command enablement
  const openDepForSelectedSurvey = selectedSurveyId ? (deployments || []).find(d => d.survey_id === selectedSurveyId && isOpenDeployment(d)) : null;
  const selectedDeployment = selectedDeploymentId ? (deployments || []).find(d => d.deployment_id === selectedDeploymentId) : null;
  const deployEnabled = Boolean(selectedSurveyId) && !openDepForSelectedSurvey && !loading;
  const undeployEnabled = Boolean(selectedDeployment && selectedDeployment.is_open) && !loading;
  const deleteEnabled = (!loading) && ((selectedSessionIds && selectedSessionIds.size > 0) || (Boolean(selectedSurveyId) && !openDepForSelectedSurvey));
  // eslint-disable-next-line no-unused-vars
  const hasExecutedForSelectedDeployment = selectedDeploymentId ? ((recipientsByDeployment.get(selectedDeploymentId) || []).some(r => r.status === 'started' || r.status === 'completed')) : false;
  const exportEnabled = Boolean(selectedDeployment) && !loading;
  // eslint-disable-next-line no-unused-vars
  const sendEnabled = Boolean(selectedDeployment && selectedRecipientIds.size > 0) && !loading;
  const editEnabled = Boolean(selectedSurveyId) && !openDepForSelectedSurvey && !loading;
  // eslint-disable-next-line no-unused-vars
  const removeLinkEnabled = Boolean(selectedDeploymentId && selectedRecipientIds.size > 0) && !loading;

  // eslint-disable-next-line no-unused-vars
  const onSend = async () => {
    if (!selectedDeployment) return;
    try {
      setLoading(true); setError('');
      // Refresh deployment users if stale
      if (usersPanelRef.current?.loadDeploymentUsers) {
        await usersPanelRef.current.loadDeploymentUsers();
      }
      const rows = [];
      for (const r of recipients) {
        if (!selectedRecipientIds.has(r.recipient_id)) continue;
        if (r.url) {
          rows.push({ email: r.email, url: r.url });
        } else {
          try {
            const created = await addRecipientInCloud({ deploymentId: selectedDeploymentId, email: r.email, name: r.name });
            rows.push({ email: r.email, url: created.url });
          } catch (_) {
            rows.push({ email: r.email, url: '(error generating URL)' });
          }
        }
      }
      setShowSendModal(rows);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };



  const surveyName = selectedSurveyId ? ((surveys || []).find(s => s.survey_id === selectedSurveyId)?.title || (surveys || []).find(s => s.survey_id === selectedSurveyId)?.name || selectedSurveyId) : '';


  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: 'var(--color-bg-base)' }}>
      {/* Bulk survey archive / delete progress overlay */}
      {(archiveProgress || deleteProgress) && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(15,23,42,0.78)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <SamSpinner
            size={72}
            text={archiveProgress ? 'Archiving surveys' : 'Deleting surveys'}
            progress={(() => { const p = archiveProgress || deleteProgress; return `${p.current} / ${p.total}`; })()}
          />
        </div>
      )}

      {/* Masthead + toolbar band come from the shared shell (§2), so the
          wordmark weight, logo placement and Help button match Designer and
          Explorer exactly. Console's existing CommandBar is passed through the
          transitional `toolbar` slot until its controls are regrouped
          left/centre/right in Console's own remediation phase. */}
      <AppShell
        wordmark="Console"
        logo={<SamLogo size={36} variant="light" />}
        help={(
          <Button
            variant="onDark"
            onClick={() => openHelpAgent({
              context: 'console',
              userId: user?.user_id,
              userName: user?.name,
              orgId: user?.org_id,
              // A used-up free account does not get the assistant. sam-dbam
              // sends the sentence to show; it is shown as it is.
              onRefused: (message) => setError(message),
            })}
            title="Open Sam Help Agent"
          >
            <HelpCircle size={16} strokeWidth={2.25} /> Help
          </Button>
        )}
        contentStyle={{ padding: 0, gap: 0, overflow: 'hidden' }}
        toolbar={<CommandBar
        loading={loading}
        refresh={refresh}
        logout={logout}
        showUsersMenu={showUsersMenu}
        setShowUsersMenu={setShowUsersMenu}
        editEnabled={editEnabled}
        deployEnabled={deployEnabled}
        undeployEnabled={undeployEnabled}
        deleteEnabled={deleteEnabled}
        exportEnabled={exportEnabled}
        selectedSurveyId={selectedSurveyId}
        selectedDeploymentId={selectedDeploymentId}
        selectedSessionIds={selectedSessionIds}
        selectedUserIds={selectedUserIds}
        selectedUsersData={selectedUsersData}
        deployments={deployments}
        openDesigner={openDesigner}
        openExplorer={openExplorer}
        openPresentor={openPresentor}
        onDeploy={onDeploy}
        onUndeploy={onUndeploy}
        onDeleteSessions={onDeleteSessions}
        onDeleteSurvey={onDeleteSurvey}
        onCopy={onCopy}
        onExportResultsCsv={onExportResultsCsv}
        onLinkSelectedUsers={onLinkSelectedUsers}
        onRevokeSelectedUsers={onRevokeSelectedUsers}
        onUnstageSelectedUsers={onUnstageSelectedUsers}
        setLoading={setLoading}
        setError={setError}
        services={statusServices}
        aggregateStatus={aggregateStatus}
        lastCheckedAt={lastCheckedAt}
        manualRefresh={manualRefresh}
        showStatusModal={showStatusModal}
        setShowStatusModal={setShowStatusModal}
        csvFormat={csvFormat}
        createSurveyInCloud={createSurveyInCloud}
        setShowSendModal={setShowSendModal}
        onEditUser={() => usersPanelRef.current?.editUser()}
        onAddUser={() => usersPanelRef.current?.createUser()}
        onManageTags={() => usersPanelRef.current?.manageTags()}
        onAssignTags={() => usersPanelRef.current?.assignTags()}
        onImportCsv={() => usersPanelRef.current?.csvImport()}
        onExportCsv={() => usersPanelRef.current?.csvExport()}
        onDeleteUsers={() => usersPanelRef.current?.deleteUsers()}
        onBrowsePopulation={() => usersPanelRef.current?.browsePopulation()}
        onBrowseSurveys={() => setShowSurveyBrowser(true)}
        onShowAccount={() => openAccountPanel('profile')}
        onShowDashboard={() => setShowDashboard(true)}
        onShowDeploymentRecipients={() => { if (selectedDeploymentId) setShowDeploymentRecipientsModal(true); }}
        onShowSendMonitor={() => staging.openSendMonitor?.()}
        sendInProgress={staging.sendInProgress}
        hasSendResult={!!staging.sendResult}
        userName={user?.name || user?.email || ''}
        userId={user?.user_id}
      />}
      >

      {showDashboard ? (
        <div style={{ flex: 1, overflow: 'auto', margin: 8, background: '#0f172a', borderRadius: 6 }}>
          <DashboardPanel orgId={user?.org_id} onDismiss={() => setShowDashboard(false)} />
        </div>
      ) : (
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden', gap: 8, padding: '8px' }}>
          <div style={{ flex: centerRightRatio, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
            {/* Channel Status sits in the space Staged gives up on its left. It
                replaces the toolbar's Channel Status dropdown. */}
            <div style={{ flex: centerRatio, overflow: 'hidden', minHeight: 0, display: 'flex', gap: 8 }}>
              <ChannelStatusPanel
                style={{ width: 200, flexShrink: 0 }}
                onError={setError}
                controls={channelControls}
                error={channelControlError}
              />
              <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                <StagingArea
                  staging={staging}
                  channelControls={channelControls}
                  onReSendSent={reSendSentSurvey}
                  onOpenRecipients={(prePopulateSelection) => usersPanelRef.current?.browsePopulation(prePopulateSelection)}
                  recipientDisplayNames={stagingRecipientDisplayNames}
                  style={{ height: '100%', boxSizing: 'border-box' }}
                />
              </div>
            </div>
            <VerticalSplitter onRatioChange={setCenterRatio} />
            <div style={{ flex: 1 - centerRatio, overflow: 'hidden', minHeight: 0 }}>
              <ExecutedSurveysPanel
                search={search}
                surveys={surveys}
                setSearch={setSearch}
                refresh={refresh}
                loading={loading}
                executedSurveys={executedSurveys}
                selectedSurveyId={selectedSurveyId}
                selectedDeploymentId={selectedDeploymentId}
                setSelectedSurveyId={setSelectedSurveyId}
                setSelectedDeploymentId={setSelectedDeploymentId}
                deployments={deployments}
                deploymentCounts={deploymentCounts}
                openDesigner={openDesigner}
                // BUG-106: wire Re-deploy
                onRedeploy={onRedeploy}
              />
            </div>
          </div>

          <HorizontalSplitter onRatioChange={setCenterRightRatio} />

          <div style={{ flex: 1 - centerRightRatio, minWidth: 280, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Deployed sits above Ready so the survey state progress reads
                counter-clockwise around the console. */}
            <div style={{ flex: rightColumnRatio, overflow: 'hidden', minHeight: 0 }}>
              <DeployedPanel
                deployments={deployments}
                surveys={surveys}
                surveyStatusById={surveyStatusById}
                onDeploy={onDeploy}
                onUndeploy={onUndeploy}
                onSetCustomSlug={onSetCustomSlug}
                selectedDeploymentId={selectedDeploymentId}
                setSelectedDeploymentId={setSelectedDeploymentId}
                setSelectedSurveyId={setSelectedSurveyId}
                onLoadRecipients={onLoadRecipients}
                // BUG-105: wire panel commands
                openDesigner={openDesigner}
              />
            </div>
            <VerticalSplitter onRatioChange={setRightColumnRatio} storageKey="console.layout.rightColSplit.v1" initialRatio={0.5} />
            <div style={{ flex: 1 - rightColumnRatio, overflow: 'hidden', minHeight: 0 }}>
              <ReadyToDeployPanel
                readyToDeploy={readyToDeploy}
                deployments={deployments}
                onUndeploy={onUndeploy}
                selectedSurveyId={selectedSurveyId}
                setSelectedSurveyId={setSelectedSurveyId}
                setSelectedDeploymentId={setSelectedDeploymentId}
                // BUG-104: wire panel commands + Duplicate
                openDesigner={openDesigner}
                onNewSurvey={onNewSurvey}
                onDeploy={onDeploy}
                onCopy={onCopy}
                onDeleteSurvey={onDeleteSurvey}
                onBulkDeleteSurveys={onBulkDeleteSurveys}
                onShareSurvey={onShareSurvey}
                onArchiveSurvey={onArchiveSurvey}
                onBulkArchiveSurveys={onBulkArchiveSurveys}
                archiveFileOpen={archiveFileOpen}
                archiveHasContent={archiveHasContent}
              />
            </div>
          </div>
        </div>
      )}

      <div style={{ position: 'fixed', left: -10000, top: 0, width: 1, height: 1, overflow: 'visible', zIndex: 5000 }}>
        <UsersPanel
          ref={usersPanelRef}
          selectedDeploymentId={selectedDeploymentId}
          surveyName={surveyName}
          openPresentor={openPresentor}
          onSelectionChange={handleUserSelectionChange}
          orgId={user?.org_id || ''}
          staging={staging}
        />
      </div>

      <NotificationBanners
        error={error}
        refresh={refresh}
        exportError={exportError}
        onExportResultsCsv={onExportResultsCsv}
        selectedDeploymentId={selectedDeploymentId}
        setExportError={setExportError}
        loading={loading}
        corsBanner={corsBanner}
        isOnline={isOnline}
      />

      {/* Modals */}
      <ConfirmModal
        open={!!confirmModal}
        title={confirmModal?.title}
        message={confirmModal?.message}
        confirmLabel={confirmModal?.confirmLabel}
        cancelLabel={confirmModal?.cancelLabel}
        danger={confirmModal?.danger}
        onConfirm={confirmModal?.onConfirm}
        onCancel={() => { confirmModal?.onCancel?.(); setConfirmModal(null); }}
      />
      {/* WP3: completion-checked undeploy confirmation. Server-decided; styled
          modal only, never a native dialog. */}
      <ConfirmModal
        open={!!undeployPrompt}
        title="Undeploy survey?"
        message={undeployPrompt ? formatUndeployConfirmation(undeployPrompt.counts) : ''}
        confirmLabel="Undeploy"
        cancelLabel="Keep live"
        danger
        onConfirm={() => resolveUndeploy(true)}
        onCancel={() => resolveUndeploy(false)}
      />
      <ArchiveOverwriteDialog
        open={!!overwriteModal}
        collisions={overwriteModal?.collisions || []}
        filename={overwriteModal?.filename}
        onConfirm={() => { overwriteModal?.resolve?.(true); setOverwriteModal(null); }}
        onCancel={() => { overwriteModal?.resolve?.(false); setOverwriteModal(null); }}
      />
      {redeployModal && (
        <SendConfirmModal
          title={`Re-deploy "${redeployModal.surveyName}" to ${redeployModal.recipients.length} recipient(s)?`}
          confirmLabel="Re-deploy"
          recipientCount={redeployModal.recipients.length}
          linkedSurveys={[{
            deploymentId: redeployModal.originalDeploymentId,
            surveyName: redeployModal.surveyName,
          }]}
          recipients={redeployModal.recipients}
          onConfirm={onConfirmRedeploy}
          onCancel={() => setRedeployModal(null)}
        />
      )}
      <PromptModal promptModal={promptModal} setPromptModal={setPromptModal} />
      <NapModal napModal={napModal} setNapModal={setNapModal} exchanges={exchanges} variableByQuestionId={variableByQuestionId} />
      <SendModal sendData={showSendModal} onClose={() => setShowSendModal(null)} />
      {showStatusModal && (
        <SystemStatusModal
          services={statusServices}
          aggregateStatus={aggregateStatus}
          lastCheckedAt={lastCheckedAt}
          onRefresh={manualRefresh}
          onClose={() => setShowStatusModal(false)}
        />
      )}

      <SurveyBrowser
        isOpen={showSurveyBrowser}
        onClose={() => setShowSurveyBrowser(false)}
        surveys={surveys}
        deployments={deployments}
        onRefresh={refresh}
        openDesigner={openDesigner}
        onDeleteSurvey={onDeleteSurvey}
        onCopy={onCopy}
        onArchiveSurvey={onArchiveSurvey}
        onBulkArchiveSurveys={onBulkArchiveSurveys}
      />

      {!billingGate.loading && !billingGate.error && isBillingAccessActive(billingGate.access) && showAccountPanel && (
        <AccountPanel
          orgId={user?.org_id}
          initialSection={accountInitialSection}
          onClose={() => setShowAccountPanel(false)}
          onArchiveStateChange={(isOpen, hasContent) => {
            setArchiveFileOpen(isOpen);
            setArchiveHasContent(hasContent);
          }}
        />
      )}

      <BillingGateOverlays
        authLoading={authLoading}
        user={user}
        billingGate={billingGate}
        refreshBillingGate={refreshBillingGate}
        logout={logout}
      />

      <DeploymentRecipientsModal
        isOpen={showDeploymentRecipientsModal}
        onClose={() => setShowDeploymentRecipientsModal(false)}
        deploymentId={selectedDeploymentId}
        surveyName={surveyName}
      />

      {/* <ExportResultsDialog
          isOpen={exportDialog}
          onClose={() => setExportDialog(false)}
          surveyName={surveyName}
          deploymentId={selectedDeploymentId}
          selectionCount={selectedSessionIds.size}
          onExport={onExportSubmit}
        /> */}
      {/* Surveys shared to this account from other accounts */}
      <IncomingSharesNotice onAccepted={refresh} onError={setError} />

      <Toast />
      </AppShell>
    </div>
  );
}
